import React, { useMemo } from "react";
import styles from "./LLMShareOfVoice.module.scss";
import PPSVG from "../../Assets/rank-tracking-assets/pp.svg";
import CLAUDESVG from "../../Assets/rank-tracking-assets/claude.svg";
import GPTSVG from "../../Assets/rank-tracking-assets/gpt.svg";
import { SOVChart } from "../../share/charts/llm-tracker/SOVChart";
import { LLMTrackerStore } from "../../../store/LLMTrackerStore";
import { getUniqueKeywordLocationPairs, isNoRank } from "./helper";
import { mergeNestedDataArrays } from "../../../helperJs/helper";

const llmOptions = {
  gpt: GPTSVG,
  claude: CLAUDESVG,
  pp: PPSVG,
};

const LLMShareOfVoice = () => {
  const {
    KEYWORD_LATEST_DATA,
    CURRLOCATION,
    USER_LLM_PROJECT,
    SELECTED_PROJECT,
  } = LLMTrackerStore((state) => state);

  const projectsByLoc = useMemo(() => {
    if (USER_LLM_PROJECT?.length === 0 || !SELECTED_PROJECT)
      return {
        allLoc: [],
        selectedLoc: [],
      };
    const selectedProjectAllLoc = USER_LLM_PROJECT?.filter(
      (project) =>
        project.weburl === SELECTED_PROJECT.weburl &&
        project.language_code === SELECTED_PROJECT.language_code
    );
    const selectedLoc =
      CURRLOCATION.name === "All Locations"
        ? selectedProjectAllLoc
        : selectedProjectAllLoc?.filter(
            (project) => project.location_name === CURRLOCATION.name
          );
    return {
      allLoc: selectedProjectAllLoc,
      selectedLoc,
    };
  }, [CURRLOCATION, SELECTED_PROJECT, USER_LLM_PROJECT]);

  const tableRawData = useMemo(() => {
    const mergedAllKeywords = getUniqueKeywordLocationPairs(
      projectsByLoc?.selectedLoc || []
    );
    if (!mergedAllKeywords?.length) return [];

    const combineKeywordData = mergeNestedDataArrays(KEYWORD_LATEST_DATA);

    // Step 1: Create a map from combineKeywordData for fast lookup
    const keywordMap = new Map();
    combineKeywordData.forEach((item) => {
      const key = `${item.keyword?.toLowerCase().trim()}|${item.location}`;
      keywordMap.set(key, item);
    });

    // Step 2: Merge - prefer items from combineKeywordData
    const result = mergedAllKeywords.map((item) => {
      const key = `${item.keyword?.toLowerCase().trim()}|${item.location}`;
      return keywordMap.get(key) || item;
    });

    return result;
  }, [KEYWORD_LATEST_DATA, projectsByLoc]);

  const sovChartData = useMemo(() => {
    if (!tableRawData?.length) {
      // Return default structure with zero values
      return {
        avgSOV: 0,
        models: [
          { name: "ChatGPT", percent: 0, color: "#5ec892ff", key: "gpt" },
          { name: "Claude", percent: 0, color: "#4e80eeff", key: "claude" },
          { name: "Perplexity", percent: 0, color: "#845eeeff", key: "pp" },
        ],
      };
    }

    const data = tableRawData.filter((item) => !item?.type);

    // Step 1: Sum values
    const total = data.reduce(
      (acc, curr) => {
        acc.gpt_sov += curr.gpt_sov || 0;
        acc.claude_sov += curr.claude_sov || 0;
        acc.pp_sov += curr.pp_sov || 0;
        return acc;
      },
      { gpt_sov: 0, claude_sov: 0, pp_sov: 0 }
    );

    // Step 2: Divide by length to get average
    const length = data.length || 1;
    const avg = {
      avg_gpt_sov: +(total.gpt_sov / length).toFixed(2),
      avg_claude_sov: +(total.claude_sov / length).toFixed(2),
      avg_pp__sov: +(total.pp_sov / length).toFixed(2),
    };

    const models = [
      {
        name: "ChatGPT",
        percent: avg.avg_gpt_sov,
        color: "#5ec892ff",
        key: "gpt",
      },
      {
        name: "Claude",
        percent: avg.avg_claude_sov,
        color: "#4e80eeff",
        key: "claude",
      },
      {
        name: "Perplexity",
        percent: avg.avg_pp__sov,
        color: "#845eeeff",
        key: "pp",
      },
    ];

    const avgSOV =
      (avg.avg_gpt_sov + avg.avg_claude_sov + avg.avg_pp__sov) /
      SELECTED_PROJECT?.llm?.length;

    return {
      models,
      avgSOV: +isNoRank(avgSOV).toFixed(2),
    };
  }, [tableRawData, SELECTED_PROJECT]);

  const highest = Math.max(...sovChartData.models.map((m) => m.percent));
  const lowest = Math.min(...sovChartData.models.map((m) => m.percent));

  return (
    <div className={styles.card}>
      <h4>LLM Share of Voice</h4>

      <div className={styles.chartSection}>
        <div className={styles.donutChart}>
          <SOVChart data={sovChartData} />
        </div>

        <div className={styles.legend}>
          {sovChartData?.models.map((model) => (
            <div key={model.name} className={styles.legendItem}>
              <div>
                <img
                  src={llmOptions[model.key]}
                  alt={llmOptions[model.key]}
                  style={{
                    objectFit: "contain",
                    width: "18px",
                  }}
                  className="me-1"
                />
                <span>{model.name}</span>
              </div>
              <span style={{ color: model.color }}>{model.percent}%</span>
            </div>
          ))}
          <div key="Highest" className={styles.legendItem}>
            <div>
              <span>Highest</span>
            </div>
            <span>{highest}%</span>
          </div>
          <div key="Lowest" className={styles.legendItem}>
            <div>
              <span>Lowest</span>
            </div>
            <span>{lowest}%</span>
          </div>
          <div key="Avg SOV" className={styles.legendItem}>
            <div>
              <span>Avg SOV</span>
            </div>
            <span>{sovChartData?.avgSOV}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LLMShareOfVoice;
